<template lang="pug">
AppPopover(
  :id="id"
  ref="AppPopover"
  :extra-popover-classes="extraPopoverClasses"
  dropdown
  @created="onCreated"
  @hide="onHide"
)
  template(#body)
    .dropdown
      .dropdown-item.time(
        v-for="(time, index) in times"
        :id="time.id"
        :key="index"
        v-tooltip="tooltipOptions"
        :class="{ selected: selected(index) }"
        data-container="body"
        :data-original-title="isDisabled(time.time)"
        @mouseover="onMouseover(index)"
        @mouseleave="onMouseleave"
        @click.stop="onClick(time.time)"
      )
        a(:disabled="isDisabled(time.time) ? 'disabled' : null")
          | {{ formatTime(time.time) }}
  template(#trigger)
    slot(name="trigger")
</template>

<script>
import { formatTime } from '~/utils/time_utils';

const FIRST_INDEX = 0;
const LAST_INDEX = 47;

export default {
  name: 'AppTimePicker',
  props: {
    type: {
      type: String,
      default: 'primary',
    },
    ceiling: {
      type: Object,
    },
    floor: {
      type: Object,
    },
    showYesterday: {
      type: Boolean,
      default: false,
    },
    time: {
      type: Object,
      default: () => moment().milliseconds(0),
    },
  },
  data: () => ({
    selectedIndex: 0,
  }),
  computed: {
    tooltipOptions() {
      return {
        placement: 'top',
        delay: 350,
      };
    },
    nearestHalfHour() {
      const time = this.showYesterday
        ? this.time.clone().seconds(0).subtract(24, 'hours')
        : this.time.clone().seconds(0);
      const remainder = time.minutes() % 30;

      return time.subtract(remainder, 'minutes');
    },
    disabledTooltip() {
      return this.type === 'timespan' ? 'Must be within time entry timespan' : 'You cannot add time in the future';
    },
    selectedTime() {
      return this.times[this.selectedIndex];
    },
    extraPopoverClasses() {
      return `vue-app-time-picker ${this.type}`;
    },
    id() {
      return `times-${this.$.uid}`;
    },
    times() {
      const baseTime = this.time.clone();

      if (this.showYesterday) {
        baseTime.subtract(24, 'hours');
      }

      let count = 0;
      const startHour = baseTime.clone().hours(0).minutes(0).seconds(0);
      const stopHour = baseTime.clone().hours(23).minutes(30).seconds(0);
      const times = [
        {
          time: startHour.clone(),
          id: `${this.timeId(count)}`,
        },
      ];

      while (startHour.add(30, 'minutes').diff(stopHour) <= 0) {
        count += 1;

        times.push({
          time: startHour.clone(),
          id: `${this.timeId(count)}`,
        });
      }

      return times;
    },
  },
  methods: {
    formatTime(time) {
      return formatTime(time, { show_seconds: 'auto' });
    },
    timeId(address) {
      return `time-${address}${this.$.uid}`;
    },
    onCreated() {
      window.addEventListener('keydown', this.keyDown);
      window.addEventListener('keyup', this.keyUp);
      this.scrollToTime();
      this.toggleVisibility(true);
    },
    onHide() {
      window.removeEventListener('keydown', this.keyDown);
      window.removeEventListener('keyup', this.keyUp);
      this.toggleVisibility(false);
    },
    toggleVisibility(visible = true) {
      this.$emit(visible ? 'open' : 'close');
    },
    scrollToTime() {
      const index = this.times.findIndex((time) => time.time.isSame(this.nearestHalfHour, 'second'));

      this.scrollToIndex(index - 1);
    },
    scrollToIndex(index) {
      if (index < 0) {
        index = 0;
      }

      this.selectedIndex = index;

      const popover = document.getElementById(this.id);

      popover.scrollTop = document.getElementById(this.selectedTime.id).offsetTop;
    },
    isDisabled(time) {
      return this.isBeforeFloor(time) || this.isAfterCeiling(time);
    },
    selected(index) {
      return this.selectedIndex === index && !this.isDisabled(this.selectedTime.time);
    },
    isBeforeFloor(time) {
      let tooltip;

      if (moment.isMoment(this.floor)) {
        tooltip = time.isBefore(this.floor) ? this.disabledTooltip : false;
      }

      return tooltip;
    },
    isAfterCeiling(time) {
      let tooltip;

      if (moment.isMoment(this.ceiling)) {
        tooltip = time.isAfter(this.ceiling) ? this.disabledTooltip : false;
      }

      return tooltip;
    },
    keyDown(e) {
      // Need this additionally to prevent window from scrolling while arrowing through the popover.
      const popover = this.$refs.AppPopover;

      if (popover && popover.showPopper) {
        e.preventDefault();
      }
    },
    keyUp(e) {
      const popover = this.$refs.AppPopover;

      if (popover && popover.showPopper) {
        e.preventDefault();
        if (e.key === 'ArrowUp') {
          if (this.selectedIndex !== FIRST_INDEX) {
            this.scrollToIndex(this.selectedIndex - 1, true);
          }
        } else if (e.key === 'ArrowDown') {
          if (this.selectedIndex !== LAST_INDEX) {
            this.scrollToIndex(this.selectedIndex + 1, true);
          }
        } else if (e.key === 'Enter') {
          this.$refs.AppPopover.close();
          this.onTimeSelect(this.selectedTime.time);
        } else if (e.key === 'Escape') {
          this.$refs.AppPopover.close();
        }
      }
    },
    onMouseover(index) {
      window.removeEventListener('keyup', this.keyUp);
      this.selectedIndex = index;
    },
    onMouseleave() {
      window.addEventListener('keyup', this.keyUp);
    },
    onClick(time) {
      if (!this.isDisabled(time)) {
        this.$refs.AppPopover.close();
      }
      this.onTimeSelect(time);
    },
    onTimeSelect(time) {
      if (!this.isDisabled(time)) {
        this.$emit('select', time);
      }
    },
  },
};
</script>
